import { useEffect, useState } from "react";
import { useNeural } from "@/store/neuralStore";
import { ArrowRight, BrainCircuit, Calendar, Database, GitBranch, Mic, Search, Sparkles, Wrench, Zap } from "lucide-react";

type Cmd = {
  id: string;
  label: string;
  group: string;
  icon: typeof Search;
  run: () => void;
};

export function CommandPalette() {
  const open = useNeural((s) => s.paletteOpen);
  const setOpen = useNeural((s) => s.setPaletteOpen);
  const setMode = useNeural((s) => s.setMode);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(!open);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
    }
  }, [open]);

  const cmds: Cmd[] = [
    { id: "m1", group: "Modes", label: "Switch to Classic Mode",  icon: BrainCircuit, run: () => setMode("classic") },
    { id: "m2", group: "Modes", label: "Switch to Game Mode",     icon: Sparkles,     run: () => setMode("game") },
    { id: "m3", group: "Modes", label: "Switch to Workflow Mode", icon: GitBranch,    run: () => setMode("workflow") },
    { id: "m4", group: "Modes", label: "Switch to 3D Space Mode", icon: Sparkles,     run: () => setMode("space") },
    { id: "m5", group: "Modes", label: "Switch to Mission Control", icon: Zap,        run: () => setMode("mission") },
    { id: "n1", group: "Navigate", label: "Open Tools Galaxy",     icon: Wrench,     run: () => navTo("tools") },
    { id: "n2", group: "Navigate", label: "Open Memory Universe",  icon: Database,   run: () => navTo("memory") },
    { id: "n3", group: "Navigate", label: "Open Scheduler",        icon: Calendar,   run: () => navTo("scheduler") },
    { id: "n4", group: "Navigate", label: "Open Sandbox",          icon: Wrench,     run: () => navTo("sandbox") },
    { id: "n5", group: "Navigate", label: "Open Agents Hub",       icon: BrainCircuit,run: () => navTo("agents") },
    { id: "n6", group: "Navigate", label: "Open Universal Chat",   icon: Sparkles,   run: () => navTo("chat") },
    { id: "n7", group: "Navigate", label: "Open Connections page",  icon: Wrench,     run: () => (window.location.href = "/connections") },
    { id: "n8", group: "Navigate", label: "Open Admin Setup",       icon: Wrench,     run: () => (window.location.href = "/admin/setup") },
    { id: "n9", group: "Navigate", label: "Open Admin Dashboard",   icon: Wrench,     run: () => (window.location.href = "/admin/dashboard") },
    { id: "a1", group: "Actions", label: "Pause All Agents",       icon: Zap,        run: () => fireEvt("pause-all") },
    { id: "a2", group: "Actions", label: "Refresh Models Now",     icon: Sparkles,   run: () => fireEvt("refresh-models") },
    { id: "a3", group: "Actions", label: "Activate Voice God Mode", icon: Mic,       run: () => fireEvt("voice-toggle") },
  ];
  const filtered = cmds.filter((c) => !q || c.label.toLowerCase().includes(q.toLowerCase()));

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] bg-background/70 backdrop-blur-sm" onClick={() => setOpen(false)}>
      <div
        className="w-[640px] max-w-[92vw] glass-strong rounded-xl overflow-hidden neural-border animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 h-12 border-b border-border">
          <Search className="w-4 h-4 text-primary" />
          <input
            autoFocus
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") setActive((a) => Math.min(a + 1, filtered.length - 1));
              if (e.key === "ArrowUp") setActive((a) => Math.max(a - 1, 0));
              if (e.key === "Enter") {
                filtered[active]?.run();
                setOpen(false);
              }
            }}
            placeholder="Type a command, navigate, or ask P2HM Agents..."
            className="flex-1 bg-transparent outline-none text-sm"
          />
          <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-surface-3 border border-border">esc</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-1">
          {filtered.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground">No commands.</div>}
          {filtered.map((c, i) => {
            const Icon = c.icon;
            return (
              <button
                key={c.id}
                onMouseEnter={() => setActive(i)}
                onClick={() => {
                  c.run();
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded text-sm text-left ${
                  active === i ? "bg-surface-3 text-foreground" : "text-muted-foreground hover:bg-surface-2"
                }`}
              >
                <Icon className="w-4 h-4 text-primary" />
                <span className="flex-1">{c.label}</span>
                <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">{c.group}</span>
                {active === i && <ArrowRight className="w-3 h-3 text-primary" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function navTo(section: string) {
  window.dispatchEvent(new CustomEvent("neural-nav", { detail: section }));
}
function fireEvt(name: string) {
  window.dispatchEvent(new CustomEvent("neural-action", { detail: name }));
}
